import { z } from 'zod';
import type { AIContextItem } from '@gitlab-org/ai-context';
import type { OptionDefMap } from './option_def';
import { tryParseAiContextItems } from './utils/ai_context_items';

const commonCommandOptionDefs = {
  existingSessionId: {
    flags: '--existing-session-id <sessionId>',
    description: 'ID of an existing session to resume.',
    env: 'DUO_WORKFLOW_WORKFLOW_ID',
    zod: z.string().optional(),
  },
} satisfies OptionDefMap;

export const tuiCommandOptionDefs = {
  ...commonCommandOptionDefs,
  importSession: {
    // No env var: set fleet-wide it would seed every new session with the same bundle.
    flags: '--import-session <path>',
    description:
      'Start a new session seeded with the summary of a session exported by `duo export`. The transcript is not replayed: the agent sees the summary only, and the session shows no prior history.',
    zod: z.string().optional(),
  },
} satisfies OptionDefMap;

export const runCommandOptionDefs = {
  goal: {
    flags: '-g, --goal <goal>',
    description: 'Goal or prompt for the session.',
    env: 'DUO_WORKFLOW_GOAL',
    mandatory: true,
    zod: z.string(),
  },
  approval: {
    flags: '--approval <decision>',
    description:
      'Human decision for a plan approval checkpoint. Use alongside `--goal` when resuming a session.',
    env: 'DUO_WORKFLOW_APPROVAL',
    choices: ['true', 'false', 'once'],
    zod: z
      .enum(['true', 'false', 'once'])
      .transform((v): 'approved' | 'rejected' => {
        if (v === 'false') return 'rejected';
        return 'approved';
      })
      .optional(),
  },
  rejectionReason: {
    flags: '--rejection-reason <text>',
    description: 'Free-text feedback explaining why the plan was rejected.',
    env: 'DUO_WORKFLOW_REJECTION_REASON',
    zod: z.string().optional(),
  },
  aiContextItems: {
    flags: '--ai-context-items <contextItems>',
    description: 'Additional context items as a JSON-encoded array.',
    env: 'DUO_WORKFLOW_ADDITIONAL_CONTEXT_CONTENT',
    parse: (value: string) => tryParseAiContextItems(value),
    zod: z.custom<AIContextItem[]>().optional(),
  },
  outputFormat: {
    flags: '--output-format <format>',
    description:
      'Output format for the run. `json` emits a single JSON document to stdout and routes logs to stderr; `text` (default) preserves the current human-readable behavior.',
    env: 'DUO_WORKFLOW_OUTPUT_FORMAT',
    choices: ['text', 'json'],
    zod: z.enum(['text', 'json']).default('text'),
  },
  ...commonCommandOptionDefs,
} satisfies OptionDefMap;

export const exportCommandOptionDefs = {
  existingSessionId: {
    ...commonCommandOptionDefs.existingSessionId,
    description: 'Required. ID of the session to export.',
    // Not `mandatory`: the root declares this flag too and consumes the value.
    zod: z.string(),
  },
  output: {
    // Resolved in the writer: the file need not exist, and --cwd is unparsed here.
    flags: '-o, --output <path>',
    description:
      'Write the bundle here instead of the default location under the Duo configuration directory.',
    zod: z.string().optional(),
  },
  outputFormat: {
    // No env var: set fleet-wide it would stop `export` writing a file.
    flags: '--output-format <format>',
    description:
      'Emit the bundle to stdout as JSON. Independent of `-o`: `json` on its own writes no file; `text` (default) writes one and prints its path.',
    choices: ['text', 'json'],
    zod: z.enum(['text', 'json']).default('text'),
  },
} satisfies OptionDefMap;
